import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { getUserErrors } from "common-client/utils/apollo";
import {
  GetCustomMapsForSettingsDocument,
  useDeleteCustomMapMutation,
} from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import { DeleteModalFieldset } from "../../../AddressPanel/Header/__styles__/DeletePropertyModal";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  PrimaryButtons,
} from "../../../Common/__styles__/Modal";
import { Button } from "../../../Common/Buttons/Button";
import Modal from "../../../Common/Modal";
import { Body, Title } from "../../../Common/Typography";
import { Checkbox } from "../../../Inputs/react-hook-form";
import { ReasonsList, ReasonsListItem } from "./__styles__/MapObjects";

interface CustomMapForDelete {
  id: string;
  name: string;
  geometryCount: number;
  canDelete: {
    deletable: boolean;
    reasons: Array<string>;
  };
}

interface DeleteConfirmationForm {
  confirmObjectsDelete: boolean;
  confirmAssociatedDataDelete: boolean;
  confirmDashboardRemoval: boolean;
}

const DeleteCustomMapModalContent = ({
  customMap,
  closeModal,
  onDelete,
}: {
  customMap: CustomMapForDelete;
  closeModal: () => void;
  onDelete?: () => void;
}) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const { deletable, reasons } = customMap.canDelete;

  const {
    formState: { isValid },
    control,
  } = useForm<DeleteConfirmationForm>({
    defaultValues: {
      confirmObjectsDelete: false,
      confirmAssociatedDataDelete: false,
      confirmDashboardRemoval: false,
    },
  });

  const [deleteCustomMap, { loading }] = useDeleteCustomMapMutation({
    refetchQueries: [GetCustomMapsForSettingsDocument],
    onCompleted: () => {
      addSuccessToast(`"${customMap.name}" deleted`);
      onDelete?.();
      closeModal();
    },
    onError: error => {
      const userError = getUserErrors(error);

      if (userError) {
        addErrorToast(userError);
      } else {
        addErrorToast(
          "An error occurred while deleting the map object. Please try again.",
        );
      }
    },
  });

  const handleDelete = async () => {
    await deleteCustomMap({ variables: { id: customMap.id } });
  };

  const objectCountLabel = `${customMap.geometryCount} ${customMap.name}`;

  return (
    <Container>
      <HeaderSection>
        <Title size="small" type="semiBold">
          Delete map object type
        </Title>
        <Body type="regular" size="large" color="contentSecondary">
          {objectCountLabel}
        </Body>
      </HeaderSection>
      <FormSection>
        <ContentSection>
          {!deletable && (
            <>
              <Body size="default" type="regular">
                This map object type can't be deleted:
              </Body>
              <ReasonsList>
                {reasons.map((reason, index) => (
                  <ReasonsListItem key={index}>
                    <Body size="default" type="regular">
                      {reason}
                    </Body>
                  </ReasonsListItem>
                ))}
              </ReasonsList>
            </>
          )}
          {deletable && (
            <>
              <Body size="default" type="regular">
                By deleting this map object type, you confirm that:
              </Body>
              <DeleteModalFieldset>
                <Checkbox
                  control={control}
                  required={true}
                  name="confirmObjectsDelete"
                  label={`All ${customMap.name} objects will be deleted.`}
                />
                <Checkbox
                  control={control}
                  required={true}
                  name="confirmAssociatedDataDelete"
                  label="All associated files, records and activity will also be deleted."
                />
                <Checkbox
                  control={control}
                  required={true}
                  name="confirmDashboardRemoval"
                  label="The records will be removed from the dashboard for all users."
                />
              </DeleteModalFieldset>
              <Body size="default" type="regular">
                This action <strong>cannot</strong> be undone.
              </Body>
            </>
          )}
        </ContentSection>
        {deletable && (
          <ButtonSection>
            <PrimaryButtons>
              <Button
                variant="secondary"
                size="medium"
                onPress={closeModal}
                title="Cancel"
              />
              <Button
                variant="critical"
                isDisabled={!isValid}
                isPending={loading}
                onPress={handleDelete}
                size="medium"
                title="Delete"
              />
            </PrimaryButtons>
          </ButtonSection>
        )}
      </FormSection>
    </Container>
  );
};

export function useDeleteCustomMapModal({
  onDelete,
}: {
  onDelete?: () => void;
}) {
  const [customMap, setCustomMap] = useState<CustomMapForDelete | null>(null);

  const [showModal, hideModal] = useModal(
    () =>
      customMap && (
        <Modal onRequestClose={hideModal}>
          <DeleteCustomMapModalContent
            customMap={customMap}
            closeModal={hideModal}
            onDelete={onDelete}
          />
        </Modal>
      ),
    [customMap, onDelete],
  );

  const showDeleteModal = (map: CustomMapForDelete) => {
    setCustomMap(map);
    showModal();
  };

  return showDeleteModal;
}
